import React from 'react';

const RefundPolicyPage: React.FC = () => {
  const lastUpdated = "January 7, 2025";

  return (
    <div className="container mx-auto px-4 py-8 text-foreground">
      <h1 className="text-4xl font-bold mb-8 text-primary text-center">Refund Policy</h1>
      <p className="mb-6 text-lg text-center text-muted-foreground">
        Last Updated: {lastUpdated}
      </p>

      <section className="mb-8 p-6 bg-card border border-border rounded-lg shadow-sm">
        <h2 className="text-2xl font-semibold mb-4 text-primary">1. Our Commitment and Your Understanding</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          At Clipoid, we are dedicated to providing a valuable and reliable digital video processing service. We encourage all users to 
          thoroughly explore our available features, free trial options (if applicable), and subscription plan details 
          before making a purchase decision. Our platform operates on a modern, consolidated architecture designed for optimal performance. 
          Understanding our services and this Refund Policy is crucial for a satisfactory experience.
        </p>
        <p className="text-muted-foreground leading-relaxed font-semibold">
          By subscribing to or purchasing any of our services, you acknowledge that you have read, understood, and agree to the terms of this Refund Policy.
        </p>
      </section>

      <section className="mb-8 p-6 bg-card border border-border rounded-lg shadow-sm">
        <h2 className="text-2xl font-semibold mb-4 text-primary">2. General No-Refund Policy for Digital Services</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          Due to the nature of digital services and the immediate access granted to our AI-powered video processing platform, features, and content upon successful payment, 
          Clipoid operates under a strict no-refund policy. 
          <strong>All sales are considered final and non-refundable once a transaction is completed.</strong>
        </p>
        <p className="text-muted-foreground leading-relaxed mb-3">
          This policy applies to all our offerings, including but not limited to:
        </p>
        <ul className="list-disc list-inside text-muted-foreground leading-relaxed ml-6 mb-3">
          <li>Subscription fees (monthly, annually, or other recurring plans).</li>
          <li>One-time purchases of specific features, credits, or add-ons.</li>
          <li>Fees for any other digital products or services offered by Clipoid.</li>
        </ul>
        <p className="text-muted-foreground leading-relaxed">
          The rationale behind this policy includes the immediate, irrevocable consumption of digital resources and access, 
          and the operational costs associated with providing and maintaining the Service.
        </p>
      </section>

      <section className="mb-8 p-6 bg-card border border-border rounded-lg shadow-sm">
        <h2 className="text-2xl font-semibold mb-4 text-primary">3. Subscription Management and Cancellations</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          <strong>3.1. User Responsibility:</strong> You are solely responsible for managing your subscription, including understanding the billing cycle and renewal dates. 
          Information regarding your subscription can typically be found in your account settings.
        </p>
        <p className="text-muted-foreground leading-relaxed mb-3">
          <strong>3.2. Cancellation Policy:</strong> You have the freedom to cancel your subscription at any time through your account dashboard or by contacting our support team.
        </p>
        <p className="text-muted-foreground leading-relaxed mb-3">
          <strong>3.3. Effect of Cancellation:</strong> Upon cancellation, your subscription will remain active, and you will continue to have full access to the paid features of the Service until the end of your current, already-paid billing period. 
          No pro-rata refunds or credits will be issued for any unused portion of your subscription term. After the current billing period ends, your access to paid features will cease, and your account may revert to a free or limited tier, if available.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          It is your responsibility to cancel your subscription prior to the renewal date if you do not wish to be charged for the next billing cycle.
        </p>
      </section>

      <section className="mb-8 p-6 bg-card border border-border rounded-lg shadow-sm">
        <h2 className="text-2xl font-semibold mb-4 text-primary">4. Exceptional Circumstances (At Clipoid's Sole Discretion)</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          While our general policy is no refunds, Clipoid may, in very rare and specific situations, and at its sole and absolute discretion, consider a refund or credit. 
          Such situations are limited to:
        </p>
        <ul className="list-disc list-inside text-muted-foreground leading-relaxed ml-6 mb-3">
          <li>Verifiable billing errors directly attributable to Clipoid's system that result in an overcharge.</li>
          <li>Prolonged, documented, and verifiable unavailability of the core Service functionalities due to issues solely on Clipoid's end, preventing you from using the Service as intended for a significant portion of your billing cycle. (Routine maintenance or minor disruptions are excluded).</li>
        </ul>
        <p className="text-muted-foreground leading-relaxed mb-3">
          Any request under exceptional circumstances must be submitted to our support team within seven (7) days of the occurrence of the alleged issue, accompanied by detailed information and supporting evidence.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          Granting a refund or credit in one instance does not obligate Clipoid to grant any future refunds or credits, nor does it waive our general no-refund policy. All such decisions are final.
        </p>
      </section>

      <section className="mb-8 p-6 bg-card border border-border rounded-lg shadow-sm">
        <h2 className="text-2xl font-semibold mb-4 text-primary">5. No Refunds For:</h2>
        <ul className="list-disc list-inside text-muted-foreground leading-relaxed ml-6">
          <li>Forgetting to cancel a subscription before the renewal date.</li>
          <li>Not using the Service or dissatisfaction with features (we encourage using trials or free tiers first).</li>
          <li>Account suspension or termination due to violation of our Terms of Use.</li>
          <li>Changes in your personal circumstances or needs.</li>
          <li>Technical issues on your end (e.g., internet connectivity, device compatibility).</li>
        </ul>
      </section>

      <section className="p-6 bg-card border border-border rounded-lg shadow-sm">
        <h2 className="text-2xl font-semibold mb-4 text-primary">6. Contact and Policy Updates</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          If you have any questions regarding this Refund Policy, or if you believe you have encountered an exceptional circumstance as outlined above, please contact our support team at support@clipoid.com.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          Clipoid reserves the right to modify this Refund Policy at any time. Any changes will be effective immediately upon posting the revised policy on our website. 
          Your continued use of the Service after such changes constitutes your acceptance of the new Refund Policy.
        </p>
      </section>
    </div>
  );
};

export default RefundPolicyPage;